import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  register as apiRegister,
  userLogin as apiLogin,
  socialLogin as apiSocialLogin,
  getCurrentUser,
  refreshToken as apiRefreshToken,
  verifyEmail as apiVerifyEmail,
  resendVerificationEmail as apiResendVerification,
  forgotPassword as apiForgotPassword,
  resetPassword as apiResetPassword,
} from '@/lib/api';
import { handleApiError, getApiErrorBody } from '@/lib/api/client';
import { useFavoriteStore } from '@/store/favoriteStore';
import { useSavedSearchStore } from '@/store/savedSearchStore';
import type { UserPublic, RegisterInput, LoginInput, SocialAuthInput, VerifyEmailInput } from '@/types';

interface UserAuthState {
  user: UserPublic | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requiresVerification: boolean;

  // Actions
  register: (input: RegisterInput) => Promise<{ success: boolean; message?: string; requiresVerification?: boolean }>;
  login: (input: LoginInput) => Promise<{ success: boolean; error?: string; requiresVerification?: boolean }>;
  socialLogin: (input: SocialAuthInput) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  verifyEmail: (input: VerifyEmailInput) => Promise<{ success: boolean; error?: string }>;
  resendVerification: (email: string) => Promise<{ success: boolean; message?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useUserAuthStore = create<UserAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      requiresVerification: false,

      register: async (input) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiRegister(input);

          set({ isLoading: false });

          if (response.success) {
            return {
              success: true,
              message: response.data?.message || 'Registration successful. Please check your email.',
              requiresVerification: response.data?.requiresVerification ?? true,
            };
          }

          set({ error: response.error || 'Registration failed' });
          return { success: false, message: response.error };
        } catch (error) {
          const message = handleApiError(error);
          set({ error: message, isLoading: false });
          return { success: false, message };
        }
      },

      login: async (input) => {
        set({ isLoading: true, error: null, requiresVerification: false });

        try {
          const response = await apiLogin(input);

          if (response.success && response.data) {
            const { user, token, refreshToken } = response.data;

            // Store token in localStorage for API client
            localStorage.setItem('userToken', token);
            if (refreshToken) {
              localStorage.setItem('userRefreshToken', refreshToken);
            }

            set({
              user,
              token,
              refreshToken: refreshToken || null,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            return { success: true };
          }

          // Check for verification required
          const responseData = response as { data?: { requiresVerification?: boolean } };
          if (responseData.data?.requiresVerification) {
            set({
              isLoading: false,
              error: response.error || 'Please verify your email',
              requiresVerification: true,
            });
            return { success: false, error: response.error, requiresVerification: true };
          }

          set({
            error: response.error || 'Login failed',
            isLoading: false,
          });
          return { success: false, error: response.error };
        } catch (error) {
          // Surface the backend's message (e.g. "Invalid email or password")
          // instead of the raw axios string "Request failed with status code 401".
          const message = handleApiError(error);
          // A 403 (email not verified) rejects the request, so the verification
          // flag must be read from the error body here, not the success path.
          if (getApiErrorBody(error)?.data?.requiresVerification) {
            set({ error: message, isLoading: false, requiresVerification: true });
            return { success: false, error: message, requiresVerification: true };
          }
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      socialLogin: async (input) => {
        set({ isLoading: true, error: null, requiresVerification: false });

        try {
          const response = await apiSocialLogin(input);

          if (response.success && response.data) {
            const { user, token, refreshToken } = response.data;

            localStorage.setItem('userToken', token);
            if (refreshToken) {
              localStorage.setItem('userRefreshToken', refreshToken);
            }

            set({
              user,
              token,
              refreshToken: refreshToken || null,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              requiresVerification: false,
            });

            return { success: true };
          }

          const message = response.error || 'Social sign-in failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        } catch (error) {
          const message = handleApiError(error);
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: () => {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userRefreshToken');
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          requiresVerification: false,
        });
        // Clear per-user data so it never carries over to the next session on a
        // shared device. (The 401-driven logout in api/client also routes here.)
        useFavoriteStore.getState().reset();
        useSavedSearchStore.getState().clearSavedSearches();
      },

      checkAuth: async () => {
        const { token, refreshToken } = get();

        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          const response = await getCurrentUser();

          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
            });
          } else {
            // Try refresh token
            if (refreshToken) {
              const refreshResponse = await apiRefreshToken(refreshToken);
              if (refreshResponse.success && refreshResponse.data?.token) {
                localStorage.setItem('userToken', refreshResponse.data.token);
                set({ token: refreshResponse.data.token });

                // Retry getting user
                const retryResponse = await getCurrentUser();
                if (retryResponse.success && retryResponse.data) {
                  set({
                    user: retryResponse.data,
                    isAuthenticated: true,
                  });
                  return;
                }
              }
            }
            get().logout();
          }
        } catch {
          get().logout();
        }
      },

      verifyEmail: async (input) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiVerifyEmail(input);

          set({ isLoading: false });

          if (response.success) {
            return { success: true };
          }

          set({ error: response.error || 'Verification failed' });
          return { success: false, error: response.error };
        } catch (error) {
          const message = handleApiError(error);
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      resendVerification: async (email) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiResendVerification(email);

          set({ isLoading: false });

          return {
            success: response.success,
            message: response.data?.message,
          };
        } catch (error) {
          const message = handleApiError(error);
          set({ error: message, isLoading: false });
          return { success: false, message };
        }
      },

      forgotPassword: async (email) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiForgotPassword(email);

          set({ isLoading: false });

          return {
            success: response.success,
            message: response.data?.message,
          };
        } catch (error) {
          const message = handleApiError(error);
          set({ error: message, isLoading: false });
          return { success: false, message };
        }
      },

      resetPassword: async (token, password) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiResetPassword(token, password);

          set({ isLoading: false });

          if (response.success) {
            return { success: true };
          }

          set({ error: response.error || 'Password reset failed' });
          return { success: false, error: response.error };
        } catch (error) {
          const message = handleApiError(error);
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'kurdacars-user-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
